import { useEffect, useRef, useState } from "react";
import {
  Check,
  Copy,
  Download,
  File,
  FileText,
  LoaderCircle,
  X,
} from "lucide-react";

export function downloadFile({ blob, fileName }) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 500);
}

export function WorkflowRecordDownloadDialog({
  recordLabel,
  busyFormat,
  error,
  onDownloadWord,
  onDownloadPdf,
  onViewText,
  onClose,
}) {
  const busy = Boolean(busyFormat);
  return (
    <div
      className="suite-modal-backdrop"
      role="presentation"
      onMouseDown={onClose}
    >
      <div
        className="suite-dialog workflow-record-download-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="workflow-record-download-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button
          className="suite-icon-button suite-dialog-close"
          type="button"
          aria-label="Cerrar"
          disabled={busy}
          onClick={onClose}
        >
          <X size={19} />
        </button>
        <span className="suite-dialog-icon workflow-record-download-icon">
          <Download size={22} />
        </span>
        <h2 id="workflow-record-download-title">Descargar registro</h2>
        <p>
          {recordLabel}. Elegí el formato del archivo.
        </p>
        {error && (
          <div className="workflow-record-download-error" role="alert">
            {error}
          </div>
        )}
        <div className="workflow-record-download-options">
          <button
            className={busyFormat === "word" ? "is-preparing" : undefined}
            type="button"
            disabled={busy}
            onClick={onDownloadWord}
          >
            <span className="workflow-record-download-format-icon">
              {busyFormat === "word"
                ? <LoaderCircle className="spin" size={20} />
                : <FileText size={20} />}
            </span>
            <span>
              <strong>
                {busyFormat === "word" ? "Preparando Word…" : "Word"}
              </strong>
              <small>Documento .docx con toda la información y desglose</small>
            </span>
          </button>
          <button
            className={busyFormat === "pdf" ? "is-preparing" : undefined}
            type="button"
            disabled={busy}
            onClick={onDownloadPdf}
          >
            <span className="workflow-record-download-format-icon">
              {busyFormat === "pdf"
                ? <LoaderCircle className="spin" size={20} />
                : <File size={20} />}
            </span>
            <span>
              <strong>
                {busyFormat === "pdf" ? "Preparando PDF…" : "PDF"}
              </strong>
              <small>Documento .pdf con toda la información y desglose</small>
            </span>
          </button>
          <button
            className={busyFormat === "text" ? "is-preparing" : undefined}
            type="button"
            disabled={busy}
            onClick={onViewText}
          >
            <span className="workflow-record-download-format-icon">
              {busyFormat === "text"
                ? <LoaderCircle className="spin" size={20} />
                : <FileText size={20} />}
            </span>
            <span>
              <strong>
                {busyFormat === "text" ? "Preparando texto…" : "Texto"}
              </strong>
              <small>Vista completa lista para copiar y pegar</small>
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}

export function WorkflowRecordTextDialog({ recordLabel, text, onClose }) {
  const textareaRef = useRef(null);
  const [copyState, setCopyState] = useState("idle");

  const selectText = () => {
    textareaRef.current?.focus();
    textareaRef.current?.select();
  };

  useEffect(() => {
    selectText();
  }, []);

  const copyText = async () => {
    setCopyState("copying");
    selectText();
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else if (!document.execCommand("copy")) {
        throw new Error("copy_failed");
      }
      setCopyState("copied");
    } catch {
      setCopyState("error");
      selectText();
    }
  };

  return (
    <div
      className="suite-modal-backdrop"
      role="presentation"
      onMouseDown={onClose}
    >
      <div
        className="suite-dialog workflow-record-text-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="workflow-record-text-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button
          className="suite-icon-button suite-dialog-close"
          type="button"
          aria-label="Cerrar"
          onClick={onClose}
        >
          <X size={19} />
        </button>
        <span className="suite-dialog-icon workflow-record-download-icon">
          <FileText size={22} />
        </span>
        <h2 id="workflow-record-text-title">Texto del registro</h2>
        <p>{recordLabel}. El contenido está seleccionado para copiarlo.</p>
        <textarea
          ref={textareaRef}
          readOnly
          aria-label={`Texto completo de ${recordLabel}`}
          value={text}
          onFocus={(event) => event.currentTarget.select()}
          onClick={(event) => event.currentTarget.select()}
        />
        {copyState === "error" && (
          <div className="workflow-record-download-error" role="alert">
            No pudimos copiar automáticamente. El texto quedó seleccionado
            para que uses el atajo de copiar.
          </div>
        )}
        <div className="workflow-record-text-actions">
          <button
            className="button secondary"
            type="button"
            onClick={onClose}
          >
            Cerrar
          </button>
          <button
            className="button primary"
            type="button"
            disabled={copyState === "copying"}
            onClick={copyText}
          >
            {copyState === "copied"
              ? <Check size={16} />
              : <Copy size={16} />}
            {copyState === "copied" ? "Copiado" : "Copiar texto"}
          </button>
        </div>
      </div>
    </div>
  );
}
